import { PageController } from '@client/components/_core/layout/PageController';
import { VDatePicker } from '@client/components/_shared/forms/view-form/VDatePicker';
import { VDropdown } from '@client/components/_shared/forms/view-form/VDropdown';
import { VReadOnly } from '@client/components/_shared/forms/view-form/VReadOnly';
import { VTextArea } from '@client/components/_shared/forms/view-form/VTextArea';
import { VTextInput } from '@client/components/_shared/forms/view-form/VTextInput';
import { FieldConfigurationViewRenderer } from '@client/components/_ui/data-entry/FieldConfigurationRenderer/FieldConfigurationViewRenderer';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { FIELD_CONFIGURATION_POLICY_SECTION_LABEL } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Col, Row } from 'antd';
import { claimFormStore } from '../store';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/policy-info')({
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <PolicyInfo />
        </PageController>
    );
}

function PolicyInfo() {
    const claim = claimFormStore.use.formData();
    const isReadonly = claimFormStore.use.isReadonly();
    const { trpcUtils } = useTrpc();
    const { formatDateTime, fDateTimeDayJs } = useFormatter();

    return (
        <Row gutter={[0, 16]}>
            <Col span={16}>
                <Card title="Policy" size="small">
                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            <VDropdown
                                isReadOnly
                                field="insurerId"
                                label="Insurer"
                                formStore={claimFormStore}
                                formatValueFn={(_, { Insurer }) => Insurer?.name}
                                loaderFn={async () => {
                                    if (!claim.productTypeId) {
                                        return [];
                                    }

                                    const data = await trpcUtils.insurer.listInsurersForSelect.fetch({
                                        where: {
                                            productTypeId: claim.productTypeId,
                                        },
                                        orderBy: {
                                            name: 'asc',
                                        },
                                    });

                                    return data.rows.map(({ id, name }) => ({
                                        value: id,
                                        label: name,
                                    }));
                                }}
                            />
                        </Col>
                        <Col span={12}>
                            <VReadOnly field="PdsVersion.Pds.name" label="PDS name" formStore={claimFormStore} />
                        </Col>
                        <Col span={12}>
                            <VReadOnly
                                field="PdsVersion.Pds.isTaxable"
                                label="Product taxable"
                                formStore={claimFormStore}
                                formatValueFn={(value) => (value ? 'Yes' : 'No')}
                            />
                        </Col>
                        <Col span={12}>
                            <VTextInput field="policyName" label="Policy name" formStore={claimFormStore} />
                        </Col>
                        <Col span={12}>
                            <VTextInput field="policyNo" label="Policy number" formStore={claimFormStore} />
                        </Col>
                        <Col span={12}>
                            <VDatePicker
                                field="policyTermFrom"
                                label="Term from"
                                formStore={claimFormStore}
                                formatValueFn={(value) => formatDateTime(value)}
                                format={fDateTimeDayJs}
                                showTime
                                maxDate={claim.policyTermTo ? claim.policyTermTo : undefined}
                                rules={[
                                    {
                                        async validator(_, value: Date) {
                                            if (!value) {
                                                throw new Error('Field is required');
                                            }

                                            if (claim.policyTermTo && value >= claim.policyTermTo) {
                                                throw new Error('Cannot be later than "Term to" date');
                                            }
                                        },
                                    },
                                ]}
                            />
                        </Col>
                        <Col span={12}>
                            <VDatePicker
                                field="policyTermTo"
                                label="Term to"
                                formStore={claimFormStore}
                                formatValueFn={(value) => formatDateTime(value)}
                                format={fDateTimeDayJs}
                                showTime
                                minDate={claim.policyTermFrom ? claim.policyTermFrom : undefined}
                                rules={[
                                    {
                                        async validator(_, value: Date) {
                                            if (!value) {
                                                throw new Error('Field is required');
                                            }

                                            if (claim.policyTermFrom && value <= claim.policyTermFrom) {
                                                throw new Error('Cannot be earlier than "Term from" date');
                                            }
                                        },
                                    },
                                ]}
                                onSave={async (value, { save, getFieldValue }) => {
                                    void save({
                                        policyTermTo: value,
                                        ...(getFieldValue('policyIssuedOn') >= value && {
                                            policyIssuedOn: null,
                                        }),
                                    });
                                }}
                            />
                        </Col>
                        <Col span={12}>
                            <VDatePicker
                                field="policyIssuedOn"
                                label="Policy issue date"
                                formStore={claimFormStore}
                                maxDate={claim.policyTermTo ? claim.policyTermTo : undefined}
                                rules={[
                                    {
                                        async validator(_, value: Date) {
                                            if (!value) {
                                                throw new Error('Field is required');
                                            }

                                            if (claim.policyTermTo && claim.policyTermTo <= value) {
                                                throw new Error('Cannot be later than "Term to" date');
                                            }
                                        },
                                    },
                                ]}
                            />
                        </Col>
                        <Col span={24}>
                            <VTextArea field="policyOtherInfo" label="Other info" formStore={claimFormStore} />
                        </Col>
                        {claim.productTypeId && (
                            <FieldConfigurationViewRenderer
                                entity="PRODUCT_TYPE"
                                entityId={claim.productTypeId} // Reference to specific ProductType
                                section={FIELD_CONFIGURATION_POLICY_SECTION_LABEL}
                                fieldPathPrefix="policyCustomFieldsData"
                                formStore={claimFormStore}
                                isReadOnly={isReadonly}
                            />
                        )}
                    </Row>
                </Card>
            </Col>
        </Row>
    );
}
